import {
    Hash,
    P2PKH,
    PrivateKey,
    TransactionSignature,
    UnlockingScript,
    Utils,
} from '@treechat/bsv-sdk'

/** Largest ordinal prevout a device will stream into a send or market signature. */
export const LOCAL_ORDINAL_MAX_SPEND_SCRIPT_BYTES = 64 * 1024 * 1024

const SIGHASH_ALL_FORKID = 0x41
const HEX_32_BYTES = /^[0-9a-f]{64}$/i
// Everything after the inscription envelope: an optional OP_CODESEPARATOR, the
// 25-byte P2PKH, and optional OP_RETURN metadata (MAP). Only the envelope is large.
const MAX_TAIL_BYTES = 64 * 1024
const YIELD_BYTES = 256 * 1024

const OP_0 = 0x00
const OP_PUSHDATA1 = 0x4c
const OP_PUSHDATA2 = 0x4d
const OP_PUSHDATA4 = 0x4e
const OP_IF = 0x63
const OP_NOTIF = 0x64
const OP_ENDIF = 0x68
const OP_RETURN = 0x6a
const OP_CODESEPARATOR = 0xab

/** An ordinal prevout locking script delivered as bytes rather than one hex string.
 *  The stream is single-use; build a new one for each signing attempt. */
export type OrdinalPrevoutScript = {
    byteLength: number
    stream: ReadableStream<Uint8Array>
}

export type ForkIdSigningTransaction = {
    txVersion: number
    lockTime: number
    inputs: Array<{ txid: string; vout: number; sequence: number }>
    outputs: Array<{ satoshis: number; script: string }>
}

export type OrdinalPrevoutSigningRequest = {
    tx: ForkIdSigningTransaction
    inputIndex: number
    satoshis: number
    script: OrdinalPrevoutScript
    expectedScriptSha256: string
    ordinalsAddress: string
    /** Market listings may spend an ordinal that already sits in a plain P2PKH output. */
    allowPlainP2PKH: boolean
    /** Omit to only verify the script's hash and layout. */
    key?: PrivateKey
}

function hexToBytes(value: string): Uint8Array {
    if (value.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(value)) {
        throw new Error('expected hexadecimal bytes')
    }
    const bytes = new Uint8Array(value.length / 2)
    for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Number.parseInt(value.slice(index * 2, index * 2 + 2), 16)
    }
    return bytes
}

function bytesToHex(value: ArrayLike<number>): string {
    return Array.from(value, byte => byte.toString(16).padStart(2, '0')).join('')
}

function hash256(value: ArrayLike<number>): number[] {
    return Hash.sha256(Hash.sha256(Array.from(value)))
}

function bytesEqual(left: ArrayLike<number>, right: ArrayLike<number>): boolean {
    if (left.length !== right.length) return false
    for (let index = 0; index < left.length; index += 1) {
        if (left[index] !== right[index]) return false
    }
    return true
}

async function yieldToBrowser(): Promise<void> {
    await new Promise<void>(resolve => {
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve())
        else setTimeout(resolve, 0)
    })
}

/** Wraps an already-hydrated hex script, e.g. a small scriptPubKey in the manifest. */
export function ordinalPrevoutScriptFromHex(scriptHex: string): OrdinalPrevoutScript {
    const bytes = hexToBytes(scriptHex)
    return {
        byteLength: bytes.length,
        stream: new ReadableStream<Uint8Array>({
            start(controller) {
                if (bytes.length) controller.enqueue(bytes)
                controller.close()
            },
        }),
    }
}

/**
 * Unwraps the server's binary prevout body: a 4-byte little-endian script
 * length followed by the script bytes, which stay a stream.
 */
export async function ordinalPrevoutScriptFromFramedStream(
    body: ReadableStream<Uint8Array>,
): Promise<OrdinalPrevoutScript> {
    const reader = body.getReader()
    let header = new Uint8Array(0)
    try {
        while (header.length < 4) {
            const { done, value } = await reader.read()
            if (done) throw new Error('ordinal prevout response ended before its length')
            const joined = new Uint8Array(header.length + value.length)
            joined.set(header)
            joined.set(value, header.length)
            header = joined
        }
    } catch (error) {
        reader.cancel().catch(() => {})
        throw error
    }
    const byteLength = header[0] + header[1] * 2 ** 8 + header[2] * 2 ** 16 + header[3] * 2 ** 24
    const leftover = header.subarray(4)
    return {
        byteLength,
        stream: new ReadableStream<Uint8Array>({
            start(controller) {
                if (leftover.length) controller.enqueue(leftover)
            },
            async pull(controller) {
                const { done, value } = await reader.read()
                if (done) controller.close()
                else controller.enqueue(value)
            },
            cancel(reason) {
                return reader.cancel(reason)
            },
        }),
    }
}

/**
 * Tracks the ordinal envelope `OP_0 OP_IF ... OP_ENDIF` byte by byte, so pushdata
 * of any size can span stream chunks. Mirrors the conditional depth rules the
 * in-memory validators used (OP_IF / OP_NOTIF open, OP_ENDIF closes).
 */
class EnvelopeScanner {
    private position = 0
    private depth = 0
    private pendingData = 0
    private lengthBytesNeeded = 0
    private lengthBytes: number[] = []
    /** Script offset just after the envelope's closing OP_ENDIF, once seen. */
    envelopeEnd = -1
    plainScript = false

    constructor(private readonly allowPlainP2PKH: boolean) {}

    /** Returns how many leading bytes of `chunk` belong to the envelope. */
    consume(chunk: Uint8Array): number {
        let index = 0
        while (index < chunk.length && this.envelopeEnd < 0 && !this.plainScript) {
            if (this.pendingData > 0) {
                const take = Math.min(this.pendingData, chunk.length - index)
                this.pendingData -= take
                index += take
                this.position += take
                continue
            }
            const byte = chunk[index]
            index += 1
            this.position += 1
            if (this.lengthBytesNeeded > 0) {
                this.lengthBytes.push(byte)
                this.lengthBytesNeeded -= 1
                if (this.lengthBytesNeeded === 0) {
                    this.pendingData = this.lengthBytes.reduce(
                        (sum, value, shift) => sum + value * 2 ** (8 * shift),
                        0,
                    )
                    this.lengthBytes = []
                }
                continue
            }
            if (this.position === 1) {
                if (byte === OP_0) continue
                if (this.allowPlainP2PKH) {
                    this.plainScript = true
                    return 0
                }
                throw new Error('ordinal prevout does not use a supported ordinal envelope layout')
            }
            if (this.position === 2 && byte !== OP_IF) {
                throw new Error('ordinal prevout does not use a supported ordinal envelope layout')
            }
            if (byte > 0 && byte < OP_PUSHDATA1) this.pendingData = byte
            else if (byte === OP_PUSHDATA1) this.lengthBytesNeeded = 1
            else if (byte === OP_PUSHDATA2) this.lengthBytesNeeded = 2
            else if (byte === OP_PUSHDATA4) this.lengthBytesNeeded = 4
            else if (byte === OP_IF || byte === OP_NOTIF) this.depth += 1
            else if (byte === OP_ENDIF) {
                this.depth -= 1
                if (this.depth === 0) this.envelopeEnd = this.position
            }
        }
        return index
    }

    finish(): void {
        if (this.plainScript) return
        if (this.pendingData > 0 || this.lengthBytesNeeded > 0) {
            throw new Error('ordinal prevout ends inside a pushdata')
        }
        if (this.envelopeEnd < 0) throw new Error('ordinal prevout has an unclosed envelope')
    }
}

function writeOutpointHashes(tx: ForkIdSigningTransaction) {
    const prevouts = new Utils.Writer()
    const sequences = new Utils.Writer()
    for (const input of tx.inputs) {
        prevouts.writeReverse(Array.from(hexToBytes(input.txid)))
        prevouts.writeUInt32LE(input.vout)
        sequences.writeUInt32LE(input.sequence)
    }
    const outputs = new Utils.Writer()
    for (const output of tx.outputs) {
        const script = Array.from(hexToBytes(output.script))
        outputs.writeUInt64LE(output.satoshis)
        outputs.writeVarIntNum(script.length)
        outputs.write(script)
    }
    return {
        hashPrevouts: hash256(prevouts.toArray()),
        hashSequence: hash256(sequences.toArray()),
        hashOutputs: hash256(outputs.toArray()),
    }
}

/** Releases a prevout stream that will not be read, e.g. after validation failed. */
export function discardOrdinalPrevoutScript(script: OrdinalPrevoutScript | undefined): void {
    if (script && !script.stream.locked) script.stream.cancel().catch(() => {})
}

/**
 * Signs one ordinal input with SIGHASH_ALL|FORKID while streaming its prevout
 * script, so an inscription of any supported size is hashed without holding it
 * (or its hex) in memory. The script must hash to the manifest's scriptSha256
 * and lock the ordinal to `ordinalsAddress` via a P2PKH after the envelope
 * (optionally followed by OP_RETURN metadata), or be that P2PKH alone when
 * `allowPlainP2PKH` is set.
 */
export async function signOrdinalPrevoutInput(
    request: OrdinalPrevoutSigningRequest & { key: PrivateKey },
): Promise<string>
export async function signOrdinalPrevoutInput(
    request: OrdinalPrevoutSigningRequest,
): Promise<string | null>
export async function signOrdinalPrevoutInput(
    request: OrdinalPrevoutSigningRequest,
): Promise<string | null> {
    const { tx, inputIndex, script } = request
    const input = tx.inputs[inputIndex]
    if (!input) throw new Error(`input ${inputIndex} is missing`)
    if (!HEX_32_BYTES.test(request.expectedScriptSha256)) {
        throw new Error('ordinal input scriptSha256 must be a SHA-256 hex digest')
    }
    if (!Number.isSafeInteger(script.byteLength) || script.byteLength < 1) {
        throw new Error('ordinal prevout script length is invalid')
    }
    if (script.byteLength > LOCAL_ORDINAL_MAX_SPEND_SCRIPT_BYTES) {
        throw new Error(`ordinal prevout exceeds ${LOCAL_ORDINAL_MAX_SPEND_SCRIPT_BYTES} bytes`)
    }

    const { hashPrevouts, hashSequence, hashOutputs } = writeOutpointHashes(tx)
    const preimagePrefix = (scriptCodeLength: number) => {
        const writer = new Utils.Writer()
        writer.writeInt32LE(tx.txVersion)
        writer.write(hashPrevouts)
        writer.write(hashSequence)
        writer.writeReverse(Array.from(hexToBytes(input.txid)))
        writer.writeUInt32LE(input.vout)
        writer.writeVarIntNum(scriptCodeLength)
        return writer.toArray()
    }
    const preimageSuffix = () => {
        const writer = new Utils.Writer()
        writer.writeUInt64LE(request.satoshis)
        writer.writeUInt32LE(input.sequence)
        writer.write(hashOutputs)
        writer.writeUInt32LE(tx.lockTime)
        writer.writeUInt32LE(SIGHASH_ALL_FORKID)
        return writer.toArray()
    }

    // The whole script is the scriptCode unless an OP_CODESEPARATOR follows the
    // envelope, which is only known after the (large) envelope has streamed past.
    const scriptHash = new Hash.SHA256()
    const fullScriptPreimage = new Hash.SHA256()
    fullScriptPreimage.update(preimagePrefix(script.byteLength))
    const scanner = new EnvelopeScanner(request.allowPlainP2PKH)
    const tail: number[] = []
    let received = 0
    let sinceYield = 0

    const reader = script.stream.getReader()
    try {
        for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            if (!value?.length) continue
            received += value.length
            if (received > script.byteLength) {
                throw new Error('ordinal prevout is longer than declared')
            }
            for (let start = 0; start < value.length; start += YIELD_BYTES) {
                const piece = value.subarray(start, Math.min(start + YIELD_BYTES, value.length))
                scriptHash.update(piece as unknown as number[])
                fullScriptPreimage.update(piece as unknown as number[])
                const envelopeBytes =
                    scanner.envelopeEnd < 0 && !scanner.plainScript ? scanner.consume(piece) : 0
                if (envelopeBytes < piece.length) {
                    if (tail.length + piece.length - envelopeBytes > MAX_TAIL_BYTES) {
                        throw new Error('ordinal prevout has an unsupported trailing script')
                    }
                    for (let index = envelopeBytes; index < piece.length; index += 1) {
                        tail.push(piece[index])
                    }
                }
                sinceYield += piece.length
                if (sinceYield >= YIELD_BYTES) {
                    sinceYield = 0
                    await yieldToBrowser()
                }
            }
        }
    } catch (error) {
        reader.cancel().catch(() => {})
        throw error
    }
    if (received !== script.byteLength) throw new Error('ordinal prevout is shorter than declared')
    scanner.finish()
    if (bytesToHex(scriptHash.digest()) !== request.expectedScriptSha256.toLowerCase()) {
        throw new Error('ordinal input script hash does not match the manifest')
    }

    const p2pkh = hexToBytes(new P2PKH().lock(request.ordinalsAddress).toHex())
    let scriptCode: number[] | null = null
    if (scanner.plainScript) {
        if (!bytesEqual(tail, p2pkh)) {
            throw new Error('ordinal prevout must be P2PKH or a supported ordinal envelope')
        }
    } else {
        const separated = tail[0] === OP_CODESEPARATOR
        const lockStart = separated ? 1 : 0
        const lock = tail.slice(lockStart, lockStart + p2pkh.length)
        const rest = tail.slice(lockStart + p2pkh.length)
        if (!bytesEqual(lock, p2pkh) || (rest.length > 0 && rest[0] !== OP_RETURN)) {
            throw new Error('ordinal prevout P2PKH tail must use the expected P2PKH locking script')
        }
        if (separated) scriptCode = tail.slice(1)
    }

    if (!request.key) return null
    let preimageHash: number[]
    if (scriptCode) {
        const preimage = new Hash.SHA256()
        preimage.update(preimagePrefix(scriptCode.length))
        preimage.update(scriptCode)
        preimage.update(preimageSuffix())
        preimageHash = preimage.digest()
    } else {
        fullScriptPreimage.update(preimageSuffix())
        preimageHash = fullScriptPreimage.digest()
    }

    const rawSignature = request.key.sign(preimageHash)
    const signature = new TransactionSignature(rawSignature.r, rawSignature.s, SIGHASH_ALL_FORKID)
    const checksig = signature.toChecksigFormat()
    return new UnlockingScript([
        { op: checksig.length, data: checksig },
        { op: 33, data: request.key.toPublicKey().encode(true) as number[] },
    ]).toHex()
}
